import React, { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { Label } from '../../components/ui/label';
import { Plus, Pencil, Trash2, Building2, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../lib/api';
import { DATA_SOURCE_LABELS, timeAgo } from '../../lib/utils';

const emptyProvider = { name: '', website: '', description: '', logo_url: '', is_active: true, is_paid: false, data_source: 'manual', affiliate_url: '' };

export default function AdminProviders() {
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyProvider);

  useEffect(() => { loadProviders(); }, []);

  const loadProviders = async () => {
    try {
      const data = await api.getProviders();
      setProviders(data);
    } catch (e) { toast.error(e.message); } finally { setLoading(false); }
  };

  const openCreate = () => { setEditing(null); setForm(emptyProvider); setDialogOpen(true); };
  const openEdit = (p) => { setEditing(p); setForm({ name: p.name, website: p.website || '', description: p.description || '', logo_url: p.logo_url || '', is_active: p.is_active, is_paid: p.is_paid, data_source: p.data_source || 'manual', affiliate_url: p.affiliate_url || '' }); setDialogOpen(true); };

  const handleSave = async () => {
    try {
      if (editing) {
        await api.updateProvider(editing.id, form);
        toast.success('Anbieter aktualisiert');
      } else {
        await api.createProvider(form);
        toast.success('Anbieter erstellt');
      }
      setDialogOpen(false);
      loadProviders();
    } catch (e) { toast.error(e.message); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Anbieter wirklich löschen? Alle zugehörigen Preise und Aktionen werden ebenfalls gelöscht.')) return;
    try {
      await api.deleteProvider(id);
      toast.success('Anbieter gelöscht');
      loadProviders();
    } catch (e) { toast.error(e.message); }
  };

  return (
    <div data-testid="admin-providers">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900" style={{fontFamily: 'Space Grotesk'}}>Anbieter</h1>
          <p className="text-sm text-muted-foreground mt-1">{providers.length} Anbieter insgesamt</p>
        </div>
        <Button onClick={openCreate} className="gap-2" data-testid="admin-crud-create-button">
          <Plus className="w-4 h-4" /> Neuer Anbieter
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sky-600" /></div>
      ) : (
        <div className="space-y-3" data-testid="admin-crud-table">
          {providers.map(p => (
            <Card key={p.id} className="rounded-xl">
              <CardContent className="p-4 flex items-center gap-4">
                <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center flex-shrink-0">
                  <Building2 className="w-5 h-5 text-slate-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-slate-900">{p.name}</span>
                    <Badge variant={p.is_active ? 'default' : 'secondary'} className="text-[10px]">{p.is_active ? 'Aktiv' : 'Inaktiv'}</Badge>
                    {p.is_paid && <Badge variant="outline" className="text-[10px] bg-sky-50 text-sky-700">Bezahlt</Badge>}
                    <Badge variant="outline" className="text-[10px]">{DATA_SOURCE_LABELS[p.data_source]}</Badge>
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    {p.website && <span>{p.website} · </span>}
                    Aktualisiert: {timeAgo(p.last_import_at || p.updated_at)}
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <Button size="sm" variant="ghost" onClick={() => openEdit(p)} data-testid="admin-edit-button"><Pencil className="w-4 h-4" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => handleDelete(p.id)} className="text-red-500 hover:text-red-700"><Trash2 className="w-4 h-4" /></Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? 'Anbieter bearbeiten' : 'Neuer Anbieter'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Name *</Label>
              <Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="z.B. IONOS" data-testid="provider-name-input" />
            </div>
            <div>
              <Label>Website</Label>
              <Input value={form.website} onChange={e => setForm({...form, website: e.target.value})} placeholder="https://..." />
            </div>
            <div>
              <Label>Beschreibung</Label>
              <Input value={form.description} onChange={e => setForm({...form, description: e.target.value})} />
            </div>
            <div>
              <Label>Affiliate-URL</Label>
              <Input value={form.affiliate_url} onChange={e => setForm({...form, affiliate_url: e.target.value})} placeholder="https://..." />
            </div>
            <div>
              <Label>Datenquelle</Label>
              <Select value={form.data_source} onValueChange={v => setForm({...form, data_source: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="manual">Manuell</SelectItem>
                  <SelectItem value="self_reported">Anbieter-Daten</SelectItem>
                  <SelectItem value="import">Importiert</SelectItem>
                  <SelectItem value="scraped">Automatisch erfasst</SelectItem>
                  <SelectItem value="api">Anbieter-API</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between">
              <Label>Aktiv</Label>
              <Switch checked={form.is_active} onCheckedChange={v => setForm({...form, is_active: v})} />
            </div>
            <div className="flex items-center justify-between">
              <Label>Bezahlt</Label>
              <Switch checked={form.is_paid} onCheckedChange={v => setForm({...form, is_paid: v})} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Abbrechen</Button>
            <Button onClick={handleSave} disabled={!form.name} data-testid="admin-save-button">Speichern</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
